import React, { useMemo, useRef } from 'react';
import ReactApexChart from 'react-apexcharts';
import moment from 'moment-timezone';
import Table from '../../../../component/table/Table';
import { OutlinedButton } from '../../../../component/buttonComponent/ButtonComponent';
import OdShareBar from '../components/OdShareBar';
import OdEcoPill from '../components/OdEcoPill';
import OdTableFooter from '../components/OdTableFooter';
import odTableStyles from '../components/odTableStyles';
import th from '../components/odColumnHeader';
import {
    buildDayboard,
    compactDuration,
    hasEnergyData,
    zeroFillDaily,
} from '../odAnalyticsUtils';

const num = (value) => <span className="od-cell-num">{value}</span>;

/**
 * A zero here is "not measured", not "none": excess idling and peak speed come from the
 * telemetry analytics endpoint, and fuel from an OBD signal half the fleet does not carry.
 * Printing 0 would claim a clean day the data cannot support. A genuine 0 km/h peak cannot
 * happen on a day that carried a trip, so nothing real is hidden by this.
 */
const orDash = (value, format) => (Number(value) > 0 ? format(Number(value)) : '—');

/**
 * A count where zero is a real answer and absent is not.
 *
 * Unlike {@link orDash}, a genuine zero here means "no harsh events that day", which is worth
 * printing. Only a missing analytics payload dashes — nobody looked, so nothing is proven clean.
 *
 * `tone` carries the event's own colour from the fleet design tokens — the same hue the Safety tab
 * badges that event type with, so a count and a row read as the same thing. Colour sits on the
 * figure alone: eighteen columns of filled chips over thirty rows is a quilt, not a table. Only a
 * non-zero count is coloured, so the days that carry events are the ones that stand out.
 */
const countCell = (value, tone) => {
    if (value === null || value === undefined) return <span className="od-cell-muted">—</span>;
    if (value === 0 || !tone) return <span className="od-cell-num">{value}</span>;
    return <span className={`od-count od-count-${tone}`}>{value}</span>;
};

/** Drive, Idle and Excess read to the second — see compactDuration. */
const exactDuration = (seconds) => compactDuration(seconds, true);

/**
 * Month-scale durations as plain hours — fleetconsole's `fmtHrs`.
 *
 * A per-day figure reads to the second because minutes matter over a shift. A month's total does
 * not: "38h 02m 54s" wraps onto two lines in a footer cell sized for a day, and nobody is counting
 * the 54 seconds across thirty days.
 */
const totalHours = (seconds) => `${(seconds / 3600).toFixed(1)} h`;

/** Where the day's litres came from — the ledger, the trip record, or a mix of both. */
const FUEL_BASIS_TITLE = {
    reconciled: 'Reconciled against the fuel ledger: in-trip refills added back, drains removed.',
    partial: 'Mixed — some trips reconciled against the fuel ledger, the rest use the trip record.',
    fallback: 'Trip record only: start level minus end level, so a mid-trip refuel reads as 0 L. A CAN-fuel vehicle has no trip-record figure at all — it reports a percentage — so a dash here means its tank capacity or fuel policy is unset.',
};

/**
 * One row per agency-local calendar day, plus a zero-filled trips-per-day chart.
 *
 * Brushing the chart publishes an epoch-millisecond range that filters every view; clicking
 * a table row toggles a day filter and jumps to the Route Map.
 */
const DayboardView = (props) => {
    const {
        trips, analyticsLoaded, episodes, events, fuelTrips, fuelEvents, speedTrips,
        fromTime, toTime, timezone, dayFilter, onSelectDay, dateRange, onBrushRange,
    } = props;

    const chartRef = useRef(null);

    const rows = useMemo(
        () => buildDayboard(trips, timezone, {
            loaded: analyticsLoaded, episodes, events, fuelTrips, fuelEvents, speedTrips,
        }),
        [trips, analyticsLoaded, episodes, events, fuelTrips, fuelEvents, speedTrips, timezone],
    );
    const series = useMemo(
        () => zeroFillDaily(trips, fromTime, toTime, timezone),
        [trips, fromTime, toTime, timezone],
    );
    const showEnergy = useMemo(() => hasEnergyData(trips), [trips]);

    // Folded from the same rows the table renders, so the footer can never disagree with the
    // column above it. A null count stays null through the sum — one unmeasured day makes the
    // whole total unmeasured, which is honest; adding it as zero would not be.
    const totals = useMemo(() => {
        const sum = (pick) => rows.reduce((total, row) => {
            const value = pick(row);
            if (value === null || value === undefined) return total;
            return total + value;
        }, 0);
        const measured = (pick) => rows.some((row) => pick(row) !== null && pick(row) !== undefined);
        const counted = (pick) => (measured(pick) ? sum(pick) : null);

        return {
            days: rows.length,
            trips: sum((row) => row.trips),
            distanceKm: sum((row) => row.distanceKm),
            driveSec: sum((row) => row.driveSec),
            idleSec: sum((row) => row.idleSec),
            excessSec: sum((row) => row.excessSec),
            accelEvents: counted((row) => row.accelEvents),
            brakeEvents: counted((row) => row.brakeEvents),
            cornerEvents: counted((row) => row.cornerEvents),
            overspeedEvents: counted((row) => row.overspeedEvents),
            fuelLitre: sum((row) => row.fuelLitre),
            energyKwh: sum((row) => row.energyKwh),
            refuels: counted((row) => row.refuels),
            drains: counted((row) => row.drains),
        };
    }, [rows]);

    // The element RDT scrolls, so the totals row can follow it. See OdTableFooter.
    const tableWrapRef = useRef(null);

    const chartOptions = useMemo(() => ({
        chart: {
            id: 'od-dayboard-chart',
            type: 'bar',
            toolbar: { show: false },
            fontFamily: 'Inter',
            animations: { enabled: false },
            zoom: { enabled: true, type: 'x', autoScaleYaxis: true },
            selection: {
                enabled: true,
                type: 'x',
                fill: { color: '#00868B', opacity: 0.12 },
                stroke: {
                    width: 1, color: '#00868B', opacity: 0.5, dashArray: 4,
                },
            },
            events: {
                zoomed: (_chart, { xaxis }) => {
                    if (!xaxis?.min || !xaxis?.max) {
                        onBrushRange(null);
                        return;
                    }
                    onBrushRange({ min: xaxis.min, max: xaxis.max });
                },
                beforeResetZoom: () => {
                    onBrushRange(null);
                },
            },
        },
        plotOptions: {
            bar: {
                borderRadius: 4,
                borderRadiusApplication: 'end',
                // Wide bars read as a calendar; thin ones read as a time series.
                columnWidth: series.length > 20 ? '85%' : '55%',
            },
        },
        colors: ['#365A6D'],
        dataLabels: { enabled: false },
        grid: { borderColor: '#EAECF0', strokeDashArray: 4 },
        xaxis: {
            type: 'datetime',
            labels: {
                formatter: (value) => moment.tz(Number(value), timezone || 'UTC').format('D MMM'),
                style: { colors: '#667085', fontSize: '12px' },
            },
            axisBorder: { color: '#EAECF0' },
            axisTicks: { color: '#EAECF0' },
        },
        yaxis: {
            min: 0,
            title: { text: 'Trips', style: { color: '#667085', fontSize: '12px', fontWeight: 500 } },
            labels: {
                formatter: (value) => Math.round(value),
                style: { colors: '#667085', fontSize: '12px' },
            },
        },
        legend: { show: false },
        tooltip: {
            x: { formatter: (value) => moment.tz(Number(value), timezone || 'UTC').format('ddd, D MMM YYYY') },
            y: { formatter: (value) => `${value} ${value === 1 ? 'trip' : 'trips'}` },
        },
        noData: { text: 'No trips in this window', style: { color: '#667085', fontFamily: 'Inter' } },
    }), [series.length, timezone, onBrushRange]);

    // Tooltip text is fleetconsole's own `data-tip` per column, except where our figure genuinely
    // differs — Drive and Idle say what they actually measure rather than repeating a header that
    // contradicts its value. Same sizing rule as the other O/D tables: header label + 20px padding
    // + the 18px RDT reserves for the sort arrow. Seventeen columns do not fit any card, so
    // `odTableStyles.table` sizes the table to its content and the wrapper scrolls.
    const columns = useMemo(() => [
        {
            name: th('Day', 'One calendar day in the selected timezone. Click a row to filter the Route Map to that day.'),
            selector: (row) => row.day,
            sortable: true,
            grow: 1.6,
            minWidth: '8rem',
            cell: (row) => (
                <div className="od-cell-stack">
                    <span className="od-cell-strong">{row.dayLabel}</span>
                    {row.day === dayFilter && <span className="od-badge od-badge-ok">Filtered</span>}
                </div>
            ),
        },
        {
            name: th('Trips', 'Journeys that began on this day. Ghost trips — below the vehicle preset’s minimum distance — are excluded.'),
            selector: (row) => row.trips,
            sortable: true,
            right: true,
            minWidth: '4.5rem',
            cell: (row) => num(row.trips),
            footer: () => totals.trips,
        },
        {
            name: th('Distance', 'Distance covered that day, in kilometres. Odometer where available, GPS otherwise.'),
            selector: (row) => row.distanceKm,
            sortable: true,
            right: true,
            minWidth: '5.75rem',
            cell: (row) => num(`${row.distanceKm.toLocaleString()} km`),
            footer: () => `${Math.round(totals.distanceKm).toLocaleString()} km`,
        },
        {
            name: th('First → last', 'First is when the vehicle first moved, not when the engine was started — a trip that idled 9 minutes in the yard reports the moment it pulled away. Last is when the engine stopped, so it includes the idling before shutdown. A dash means the day carried no journey at all, only idling or events.'),
            // Sorts a tripless day to the top ascending, where a day with nothing to report belongs.
            selector: (row) => row.firstTs ?? 0,
            sortable: true,
            grow: 1.4,
            minWidth: '10.5rem',
            cell: (row) => num(row.firstLast),
        },
        {
            name: th('Drive', 'Time inside a trip with the vehicle actually moving — the trip’s duration less its idling. fleetconsole’s column of this name reports whole trip duration instead, so it reads higher.'),
            selector: (row) => row.driveSec,
            sortable: true,
            right: true,
            minWidth: '6.5rem',
            cell: (row) => num(exactDuration(row.driveSec)),
            footer: () => totalHours(totals.driveSec),
        },
        {
            name: th('Idle', 'Engine-on stationary time overlapping this day, including idling between trips. A run spanning midnight is split across both days by overlap, so a 24-hour day can never report more than 24 hours.'),
            selector: (row) => row.idleSec,
            sortable: true,
            right: true,
            minWidth: '6.5rem',
            cell: (row) => num(orDash(row.idleSec, exactDuration)),
            footer: () => orDash(totals.idleSec, totalHours),
        },
        {
            name: th('Idle share', 'Idling as a fraction of engine-on time — the Idle and Drive columns beside it, which are disjoint and sum to engine-on time.'),
            selector: (row) => row.idleSharePct,
            sortable: true,
            grow: 1.4,
            minWidth: '7rem',
            cell: (row) => <OdShareBar idlePct={row.engineOnSec > 0 ? row.idleSharePct : null} />,
        },
        {
            name: th('Excess', 'Idle time beyond this vehicle preset’s excessive-idle limit, apportioned to the days the episode actually covered.'),
            selector: (row) => row.excessSec,
            sortable: true,
            right: true,
            minWidth: '6.5rem',
            cell: (row) => num(orDash(row.excessSec, exactDuration)),
            footer: () => orDash(totals.excessSec, totalHours),
        },
        {
            name: th('Peak', 'Highest speed recorded on that day’s trips, km/h. The fastest moment, not the fastest flagged one.'),
            selector: (row) => row.peakKmh,
            sortable: true,
            right: true,
            minWidth: '4.75rem',
            cell: (row) => num(orDash(row.peakKmh, (value) => `${Math.round(value)}`)),
        },
        {
            name: th('A', 'Harsh ACCELERATION events.'),
            selector: (row) => row.accelEvents ?? -1,
            sortable: true,
            right: true,
            minWidth: '3.5rem',
            cell: (row) => countCell(row.accelEvents, 'accel'),
            footer: () => countCell(totals.accelEvents, 'accel'),
        },
        {
            name: th('B', 'Harsh BRAKING events.'),
            selector: (row) => row.brakeEvents ?? -1,
            sortable: true,
            right: true,
            minWidth: '3.5rem',
            cell: (row) => countCell(row.brakeEvents, 'brake'),
            footer: () => countCell(totals.brakeEvents, 'brake'),
        },
        {
            name: th('C', 'Harsh CORNERING events.'),
            selector: (row) => row.cornerEvents ?? -1,
            sortable: true,
            right: true,
            minWidth: '3.5rem',
            cell: (row) => countCell(row.cornerEvents, 'corner'),
            footer: () => countCell(totals.cornerEvents, 'corner'),
        },
        {
            name: th('Speed', 'Overspeeding episodes.'),
            selector: (row) => row.overspeedEvents ?? -1,
            sortable: true,
            right: true,
            minWidth: '5rem',
            cell: (row) => countCell(row.overspeedEvents, 'speed'),
            footer: () => countCell(totals.overspeedEvents, 'speed'),
        },
        {
            name: th('Eco 0–10', 'Device-reported eco-driving score for that day, distance-weighted so a long trip counts for more than a short one. Higher is better — the opposite direction to every other coloured column here.'),
            selector: (row) => row.ecoScore ?? -1,
            sortable: true,
            right: true,
            minWidth: '6.5rem',
            cell: (row) => <OdEcoPill score={row.ecoScore} />,
        },
        {
            name: th('Fuel L', 'Fuel burnt that day, litres. Reconciled against the ledger where it could be — hover a value for its basis.'),
            selector: (row) => row.fuelLitre,
            sortable: true,
            right: true,
            minWidth: '5rem',
            cell: (row) => (
                <span className="od-cell-num" title={FUEL_BASIS_TITLE[row.fuelBasis]}>
                    {orDash(row.fuelLitre, (value) => value.toLocaleString())}
                </span>
            ),
            footer: () => orDash(totals.fuelLitre, (value) => Math.round(value).toLocaleString()),
        },
        showEnergy && {
            name: th('Energy', 'Energy used that day, kWh. EV and hybrid only.'),
            selector: (row) => row.energyKwh,
            sortable: true,
            right: true,
            minWidth: '5.5rem',
            cell: (row) => num(`${row.energyKwh.toLocaleString()} kWh`),
            footer: () => `${Math.round(totals.energyKwh).toLocaleString()} kWh`,
        },
        {
            name: th('Refuels', 'Refuelling events detected in the fuel signal that day.'),
            selector: (row) => row.refuels ?? -1,
            sortable: true,
            right: true,
            minWidth: '5.5rem',
            cell: (row) => countCell(row.refuels),
            footer: () => countCell(totals.refuels),
        },
        {
            name: th('Drains', 'Sudden unexplained fuel drops flagged as possible theft.'),
            selector: (row) => row.drains ?? -1,
            sortable: true,
            right: true,
            minWidth: '5.25rem',
            cell: (row) => countCell(row.drains, 'error'),
            footer: () => countCell(totals.drains, 'error'),
        },
    ].filter(Boolean), [dayFilter, showEnergy, totals]);

    const rowStyles = useMemo(() => [{
        when: (row) => row.day === dayFilter,
        style: { backgroundColor: '#F9FAFB', boxShadow: 'inset 0.1875rem 0 0 #365A6D' },
    }], [dayFilter]);

    const resetZoom = () => {
        // Chart ref, not ApexCharts.getChartByID — the global registry throws when the
        // chart has not mounted yet.
        chartRef.current?.chart?.resetSeries?.();
        chartRef.current?.chart?.zoomX?.(
            series[0]?.[0],
            series[series.length - 1]?.[0],
        );
        onBrushRange(null);
    };

    return (
        <div className="od-view">
            <div className="od-card">
                <div className="od-card-header">
                    <div className="od-card-header-text">
                        <div className="od-card-title">Trips per day</div>
                        <div className="od-card-subtitle">
                            Drag across the chart to narrow every view to that window
                        </div>
                    </div>
                    <div className="od-card-actions">
                        <OutlinedButton label="Reset zoom" width="7rem" onClickFunction={resetZoom} disable={!dateRange} />
                    </div>
                </div>
                <div className="od-card-body od-chart">
                    <ReactApexChart
                      ref={chartRef}
                      options={chartOptions}
                      series={[{ name: 'Trips', data: series }]}
                      type="bar"
                      height={240}
                    />
                </div>
                <div className="od-card-note">
                    Days with no trips are shown as zero. The API returns only hours that carry
                    trips, so an unfilled series would make a rest day look like missing data.
                </div>
            </div>

            <div className="od-card">
                <div className="od-card-header">
                    <div className="od-card-header-text">
                        <div className="od-card-title">Daily breakdown</div>
                        <div className="od-card-subtitle">
                            {`One row per calendar day in ${timezone || 'UTC'} · click a row to filter the Route Map to that day`}
                        </div>
                    </div>
                </div>
                {/* Every day on one scroll with a sticky header — Route Atlas's .pbody,
                    not a 15-row page that hides most of the window. */}
                <div className="od-card-body od-table-wrap" ref={tableWrapRef}>
                    <Table
                      tableData={rows}
                      columns={columns}
                      module="odAnalytics"
                      fixedHeaderScrollHeight="32rem"
                      handlePageChange={() => {}}
                      handlePerRowsChange={() => {}}
                      setSelectedRowCount={() => {}}
                      totalRows={rows.length}
                      customStyles={odTableStyles}
                      rowStyles={rowStyles}
                      onRowClicked={(row) => onSelectDay(row.day === dayFilter ? null : row.day)}
                      highlightOnHover
                      pointerOnHover
                    />
                </div>
                {rows.length > 0 && (
                    <OdTableFooter
                      columns={columns}
                      label={`${totals.days} ${totals.days === 1 ? 'day' : 'days'}`}
                      scrollerRef={tableWrapRef}
                    />
                )}
                <div className="od-card-note">
                    A day appears if it carried any activity, not only a journey — an asset that sat
                    all day with the engine running shows a row with no trips and its idling
                    intact. First is the day&apos;s first movement, not its first ignition-on; last
                    is the engine stopping, idling included. Drive is time spent moving and Idle is
                    engine-on stationary time, so the two are disjoint and sum to engine-on time —
                    which is what Idle share divides by. Idling that spans midnight is split across
                    both days by overlap rather than credited whole to the day it began. Excess,
                    Peak, the A / B / C / Speed counts and the fuel-event columns come from the
                    telemetry engines, so a dash there means unmeasured, not zero.
                </div>
            </div>
        </div>
    );
};

export default DayboardView;
